import { useState, useEffect } from 'react'
import { message } from 'antd'

export const useBiometrics = ({
    caseDetails,
    form,
    setLoading,
    onSuccess,
    apiClasses,
    apiType = 'physician'
}) => {
    const [initialValues, setInitialValues] = useState(null)

    const handleSubmit = async (formValues, newAllergyQueue, patientAllergies) => {
        const isAdmin = apiType === 'admin'
        const hasUpdateMethod = isAdmin 
            ? apiClasses?.PhysicianAPI?.updateCasePatientDetails 
            : apiClasses?.PhysicianAPI?.updateMedicalInformation
            
        if (!hasUpdateMethod) {
            message.error('Update functionality not available')
            return
        }

        setLoading(true)
        
        try {
            let payload
            
            if (isAdmin) {
                payload = {
                    height: formValues.height ? 1 : undefined, 
                    heightIn: formValues.height,
                    weight: formValues.weight,
                    bloodPressure: formValues.bloodPressure,
                    medications: formValues.medications,
                    allergies: formValues.allergies
                }
            } else {
                payload = { ...formValues }
                
                if (payload.bloodPressure !== undefined) {
                    payload['blood-pressure'] = payload.bloodPressure
                    delete payload.bloodPressure
                }
                
                payload.newAllergyQueue = newAllergyQueue || []
            }
                
            Object.keys(payload).forEach(key => {
                if (payload[key] === undefined) {
                    delete payload[key]
                }
            })
            
            const caseId = caseDetails?.id || caseDetails?._id || caseDetails?.caseId
            
            if (!caseId) {
                message.error('Case ID not found - cannot update medical information')
                return
            }
            
            const response = isAdmin 
                ? await apiClasses.PhysicianAPI.updateCasePatientDetails(caseId, payload)
                : await apiClasses.PhysicianAPI.updateMedicalInformation(payload, caseId)

            if (response.data?.success) {
                message.success('Medical information updated successfully!')
                form.resetFields()
                onSuccess?.()
            } else {
                message.error(response.data?.payload || 'Failed to update medical information')
            }
        } catch (error) {
            console.error('Error updating medical information:', error)
            console.error('Error response:', error.response)
            console.error('Error response data:', error.response?.data)
            const errorMessage = error.response?.data?.message || 
                                error.response?.data?.error ||
                                'Unable to update medical information, please try again!'
            message.error(errorMessage)
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        form.resetFields()
        if (initialValues) {
            form.setFieldsValue(initialValues)
        }
    }

    // Set initial values when case details change
    useEffect(() => {
        if (caseDetails) {
            const formData = {
                height: caseDetails?.heightFt ? caseDetails.heightFt * 12 + (Number(caseDetails.heightIn) || 0) : null,
                weight: caseDetails?.weight || null,
                bloodPressure: caseDetails?.bloodPressure || '',
                medications: caseDetails?.medications || '',
                allergies: caseDetails?.allergies || '' // Patient-reported allergies string field
            }
            setInitialValues(formData)
        }
    }, [caseDetails])

    return {
        initialValues,
        handleSubmit,
        handleCancel
    }
}
